<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <review-confirm-modal ref="confirmDialog"></review-confirm-modal>
    <div class="orangehrm-card-container">
      <oxd-text tag="h5" class="orangehrm-performance-review-title">
        {{ $t('performance.performance_review') }}
      </oxd-text>
    </div>
    <br />
    <review-summary
      :loading="isLoading"
      :status="status"
      :due-date="dueDate"
      :employee="employee.details"
      :job-title="employee.jobTitle"
      :review-period-end="reviewPeriodEnd"
      :review-period-start="reviewPeriodStart"
    />
    <br />
    <oxd-form ref="formRef" :loading="isLoading">
      <div v-if="status < 4">
        <template v-if="showSupervisorEvaluation">
          <evaluation-form
            v-model="supervisorReview"
            :kpis="kpis"
            :kpi-categories="kpiCategories"
            :rules="rules"
            :editable="false"
            :collapsible="true"
            :collapsed="true"
            :employee="supervisor.details"
            :job-title="supervisor.jobTitle"
            :status="supervisor.status"
            :title="$t('performance.supervisor_evaluation_by')"
          >
            <oxd-divider />
            <kpi-calculation-summary
              :merged-data="mergedDataAdm"
            ></kpi-calculation-summary>
          </evaluation-form>
          <br />
        </template>
        <evaluation-form
          v-if="enableSelfReview"
          v-model="employeeReview"
          :kpis="kpis"
          :kpi-categories="kpiCategories"
          :rules="rules"
          :editable="employee.status < 3"
          :collapsed="false"
          :collapsible="true"
          :employee="employee.details"
          :job-title="employee.jobTitle"
          :status="employee.status"
          :title="$t('performance.self_evaluation_by')"
        >
          <oxd-divider />
          <kpi-calculation-summary
            :merged-data="mergedDataEmp"
          ></kpi-calculation-summary>
          <final-evaluation
            v-model:final-rating="finalRatingEmp"
            v-model:final-score="finalScoreEmp"
            :status="status"
            :hidden-some="true"
            :is-required="false"
          />
          <oxd-form-actions v-show="hasActions">
            <oxd-divider />
            <div class="orangehrm-performance-review-actions">
              <oxd-button
                v-if="hasCancelAction"
                display-type="ghost"
                :label="$t('general.cancel')"
                @click="onClickCancel"
              />
              <oxd-button
                v-if="hasSaveAction"
                display-type="ghost"
                type="button"
                :label="$t('general.save')"
                @click="onSubmit(false)"
              />
              <oxd-button
                v-if="hasCompleteAction"
                type="button"
                display-type="secondary"
                :label="$t('performance.complete')"
                @click="onSubmit(true)"
              />
            </div>
          </oxd-form-actions>
        </evaluation-form>
      </div>
      <div v-if="status === 4">
        <evaluation-form
          v-if="enableSelfReview"
          v-model="employeeReview"
          :kpis="kpis"
          :kpi-categories="kpiCategories"
          :rules="rules"
          :editable="false"
          :collapsed="true"
          :collapsible="true"
          :employee="employee.details"
          :job-title="employee.jobTitle"
          :status="employee.status"
          :title="$t('performance.self_evaluation_by')"
        >
          <oxd-divider />
          <kpi-calculation-summary
            :merged-data="mergedDataEmp"
          ></kpi-calculation-summary>
          <final-evaluation
            v-model:final-rating="finalRatingEmp"
            v-model:final-score="finalScoreEmp"
            :status="status"
            :hidden-some="true"
            :is-required="false"
          />
        </evaluation-form>
        <template v-if="showSupervisorEvaluation || !enableSelfReview">
          <br v-if="enableSelfReview" />
          <evaluation-form
            v-model="supervisorReview"
            :kpis="kpis"
            :kpi-categories="kpiCategories"
            :rules="rules"
            :editable="false"
            :collapsible="true"
            :collapsed="true"
            :employee="supervisor.details"
            :job-title="supervisor.jobTitle"
            :status="supervisor.status"
            :hidden-some="true"
            :title="$t('performance.supervisor_evaluation_by')"
          >
            <oxd-divider />
            <kpi-calculation-summary
              :merged-data="mergedDataAdm"
            ></kpi-calculation-summary>
            <final-evaluation
              v-model:final-rating="finalRatingAdm"
              v-model:final-comment="finalComment"
              v-model:completed-date="completedDate"
              v-model:final-score="finalScoreAdm"
              :status="status"
              :hidden-some="false"
              :is-required="false"
            />
          </evaluation-form>
        </template>
      </div>
    </oxd-form>
  </div>
</template>

<script>
import {navigate, reloadPage} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import ReviewSummary from '../components/ReviewSummary';
import FinalEvaluation from '../components/FinalEvaluation';
import EvaluationForm from '../components/EvaluationForm';
import useForm from '@ohrm/core/util/composable/useForm';
import useReviewEvaluation from '@/orangehrmPerformancePlugin/util/composable/useReviewEvaluation';
import {summarizePerformanceScore} from '@/orangehrmPerformancePlugin/util/performanceScore';
import ReviewConfirmModal from '@/orangehrmPerformancePlugin/components/ReviewConfirmModal';
import {ref, watch} from 'vue';
import KpiCalculationSummary from '@/orangehrmPerformancePlugin/components/KpiCalculationSummary.vue';

const reviewerModel = {
  details: {
    empNumber: null,
    firstName: '',
    lastName: '',
    terminationId: null,
  },
  jobTitle: '',
  status: 1,
  actions: new Map(),
};

export default {
  name: 'SelfEvaluation',
  components: {
    'kpi-calculation-summary': KpiCalculationSummary,
    'review-summary': ReviewSummary,
    'final-evaluation': FinalEvaluation,
    'evaluation-form': EvaluationForm,
    'review-confirm-modal': ReviewConfirmModal,
  },
  props: {
    reviewId: {
      type: Number,
      required: true,
    },
    status: {
      type: Number,
      required: true,
    },
    reviewPeriodStart: {
      type: String,
      required: true,
    },
    reviewPeriodEnd: {
      type: String,
      required: true,
    },
    dueDate: {
      type: String,
      required: true,
    },
    enableSelfReview: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const {formRef, invalid, validate} = useForm();
    const http = new APIService(window.appGlobal.baseUrl, '');

    const {
      getAllKpis,
      getEmployeeReview,
      getSupervisorReview,
      getFinalReview,
      generateRules,
      generateModel,
      generateReviewerData,
      generateAllowedActions,
      generateEvaluationFormData,
      finalizeReview,
      saveEmployeeReview,
      flattenKPIs,
      parseReviewRating,
    } = useReviewEvaluation(http);

    return {
      http,
      invalid,
      formRef,
      validate,
      getAllKpis,
      generateRules,
      generateModel,
      generateReviewerData,
      generateAllowedActions,
      generateEvaluationFormData,
      getEmployeeReview,
      getSupervisorReview,
      getFinalReview,
      finalizeReview,
      saveEmployeeReview,
      flattenKPIs,
      parseReviewRating,
    };
  },
  data() {
    const supervisorReview = ref({});
    const mergedDataAdm = ref([]);
    const finalRatingAdm = ref(0);
    const finalScoreAdm = ref('');

    const employeeReview = ref({});
    const mergedDataEmp = ref([]);
    const finalRatingEmp = ref(0);
    const finalScoreEmp = ref('');
    watch(
      supervisorReview,
      (newVal) => {
        mergedDataAdm.value = this.filterSummaryOne(this.kpiCategories, newVal);
        finalRatingAdm.value = this.sumTotalValueTimesWeight(
          mergedDataAdm.value,
        );
        finalScoreAdm.value = this.summarizeResult(finalRatingAdm.value);
      },
      {deep: true, immediate: true},
    );
    watch(
      employeeReview,
      (newVal) => {
        mergedDataEmp.value = this.filterSummaryOne(
          this.kpiCategoriesEmployee,
          newVal,
        );
        finalRatingEmp.value = this.sumTotalValueTimesWeight(
          mergedDataEmp.value,
        );
        finalScoreEmp.value = this.summarizeResult(finalRatingEmp.value);
      },
      {deep: true, immediate: true},
    );
    return {
      kpis: [], // Flattened KPIs for grouping false
      kpiCategories: [], // Grouped KPIs for grouping true
      kpisEmployee: [], // Flattened KPIs for grouping false
      kpiCategoriesEmployee: [], // Grouped KPIs for grouping true
      rules: [],
      employee: {...reviewerModel},
      isLoading: false,
      employeeReview,
      supervisor: {...reviewerModel},
      supervisorReview,
      mergedDataAdm,
      finalRatingAdm,
      finalScoreAdm,
      mergedDataEmp,
      finalRatingEmp,
      finalScoreEmp,
      finalComment: null,
      completedDate: null,
    };
  },
  computed: {
    showSupervisorEvaluation() {
      if (!this.enableSelfReview) {
        return this.status === 4;
      }
      return this.supervisor.status === 3;
    },
    hasSaveAction() {
      return this.employee.actions.has('save');
    },
    hasCompleteAction() {
      return this.employee.actions.has('complete');
    },
    hasCancelAction() {
      return !(this.status === 4 || this.employee?.status === 3);
    },
    hasActions() {
      return (
        this.hasSaveAction || this.hasCancelAction || this.hasCompleteAction
      );
    },
  },
  beforeMount() {
    this.isLoading = true;
    this.getAllKpis(this.reviewId, true)
      .then((response) => {
        const {data} = response.data;
        if (data.length > 0 && 'categoryId' in data[0]) {
          this.kpiCategories = [...data];
          this.kpis = this.flattenKPIs(data);

          // Employee
          this.kpiCategoriesEmployee = [...data];
          this.kpisEmployee = this.flattenKPIs(data);
        } else {
          this.kpis = [...data];
          this.kpiCategories = [];

          // Employee
          this.kpis = [...data];
          this.kpiCategories = [];
        }

        this.rules = this.generateRules(data);
        this.employeeReview = this.generateModel(data);
        this.supervisorReview = this.generateModel(data);
        return this.getEmployeeReview(this.reviewId);
      })
      .then((response) => {
        const {data} = response.data;
        const {meta} = response.data;
        this.employee = this.generateReviewerData(meta.reviewer);
        this.employee.actions = this.generateAllowedActions(
          meta.allowedActions,
        );
        this.employeeReview = this.generateEvaluationFormData(
          data,
          meta.generalComment,
          this.employeeReview.kpis,
        );
        return this.getSupervisorReview(this.reviewId);
      })
      .then((response) => {
        const {data} = response.data;
        const {meta} = response.data;
        this.supervisor = this.generateReviewerData(meta.reviewer);
        this.supervisor.actions = this.generateAllowedActions(
          meta.allowedActions,
        );
        this.supervisorReview = this.generateEvaluationFormData(
          data,
          meta.generalComment,
          this.supervisorReview.kpis,
        );
        return this.status === 4 ? this.getFinalReview(this.reviewId) : {};
      })
      .then((response) => {
        if (Object.keys(response).length !== 0) {
          const {data} = response.data;
          this.finalRating = data.finalRating;
          this.finalComment = data.finalComment;
          this.completedDate = data.completedDate;
        }
      })
      .finally(() => {
        this.calculateInitialValues(
          'adm',
          this.kpiCategories,
          this.supervisorReview,
          this.mergedDataAdm,
        );
        this.calculateInitialValues(
          'emp',
          this.kpiCategoriesEmployee,
          this.employeeReview,
          this.mergedDataEmp,
        );
        this.isLoading = false;
      });
  },
  methods: {
    calculateInitialValues(type, kpis, reviewer, mergedData) {
      if (type === 'adm') {
        setTimeout(
          () => {
            this.mergedDataAdm = this.filterSummaryOne(kpis, reviewer);
            this.finalRatingAdm = this.sumTotalValueTimesWeight(mergedData);
            this.finalScoreAdm = this.summarizeResult(this.finalRatingAdm);
          },
          this.supervisor.status === 3 ? 0 : 1000,
        );
      } else {
        setTimeout(
          () => {
            this.mergedDataEmp = this.filterSummaryOne(kpis, reviewer);
            this.finalRatingEmp = this.sumTotalValueTimesWeight(mergedData);
            this.finalScoreEmp = this.summarizeResult(this.finalRatingEmp);
          },
          this.employee.status === 3 ? 0 : 1000,
        );
      }
    },
    filterSummaryOne(dataTarget, dataFrom) {
      if (!dataTarget || !Array.isArray(dataTarget)) return [];

      return dataTarget.map((category) => {
        let totalKpi = 0;
        let totalKpiIfAllMaxed = 0;

        if (category.subCategory) {
          category.subCategory.forEach((sub) => {
            if (sub.kpi) {
              sub.kpi = sub.kpi.map((kpi) => {
                if (typeof kpi.maxRating === 'number') {
                  totalKpiIfAllMaxed += kpi.maxRating;
                }
                const found = dataFrom.kpis?.find((k) => k.kpiId === kpi.id);
                if (found) {
                  kpi.rating = found.rating;
                  totalKpi += this.parseReviewRating(found.rating);
                } else {
                  kpi.rating = null;
                }
                return kpi;
              });
            }
          });
        }

        // Total Value
        const totalValueNum =
          totalKpiIfAllMaxed > 0 ? (totalKpi / totalKpiIfAllMaxed) * 100 : 0;
        const safeTotalValueNum = Number.isFinite(totalValueNum)
          ? totalValueNum
          : 0;
        const totalValue = Number(safeTotalValueNum.toFixed(2)).toString();
        // Total Value times weight
        const totalValueTimesWeightNum =
          category.weight > 0 ? (safeTotalValueNum * category.weight) / 100 : 0;
        const safeTotalValueTimesWeightNum = Number.isFinite(
          totalValueTimesWeightNum,
        )
          ? totalValueTimesWeightNum
          : 0;
        const totalValueTimesWeight = Number(
          safeTotalValueTimesWeightNum.toFixed(2),
        ).toString();

        return {
          ...category,
          totalKpi: totalKpi.toString(),
          totalKpiIfAllMaxed: totalKpiIfAllMaxed.toString(),
          totalValue: totalValue,
          totalValueTimesWeight: totalValueTimesWeight,
          generalComment: dataFrom.generalComment || '',
        };
      });
    },

    sumTotalValueTimesWeight(categories) {
      if (!Array.isArray(categories)) return 0;

      return categories.reduce((sum, category) => {
        const value = parseFloat(category.totalValueTimesWeight);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
    },

    summarizeResult(value) {
      return summarizePerformanceScore(value);
    },
    onSubmit(complete = false) {
      this.$nextTick()
        .then(() => this.validate())
        .then(async () => {
          if (this.invalid === true) return;
          if (complete) {
            this.$refs.confirmDialog.showDialog().then((confirmation) => {
              if (confirmation === 'ok') {
                this.submitReview(true);
              }
            });
          } else {
            this.submitReview(false);
          }
        });
    },
    submitReview(complete = false) {
      this.isLoading = true;
      this.saveEmployeeReview(this.reviewId, complete, this.employeeReview)
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .finally(() => {
          reloadPage();
        });
    },
    onClickCancel() {
      navigate('/performance/myPerformanceReview');
    },
  },
};
</script>

<style src="./review-evaluate.scss" lang="scss" scoped></style>
